import { useMutation, useQueryClient } from '@tanstack/react-query'
import _ from 'lodash'
import env from 'src/env'
import { getHttpInit } from 'src/utils/api'
import { DEFAULT_ERROR_HANDLER, handleErrorResponse } from 'src/utils/apiUtils'

/**
 * @param {Object} params
 * @param {Object} params.viewAccount
 * @param {Array} params.contactList
 * @param {() => void} params.onSuccess
 * @param {(alert: Object) => void} params.setAlertComponent
 */
const useProfileSubmit = ({ viewAccount, contactList, onSuccess, setAlertComponent }) => {
  const queryClient = useQueryClient()

  const getContactType = (contact) => contact?.name || ''

  const getCurrentContact = () =>
    contactList?.length && viewAccount?.SecondaryContact
      ? contactList.find((contact) => contact.id === viewAccount.SecondaryContact)
      : null

  const currentContact = getCurrentContact()

  const defaultValues = currentContact
    ? {
        firstName: currentContact.firstname || '',
        lastName: currentContact.lastname || '',
        email: currentContact.email || '',
        officeNumber: currentContact.phone || '',
        mobileNumber: currentContact.mobile || '',
        position: currentContact.title || '',
      }
    : {}

  const updateContactInfo = useMutation({
    retry: false,
    mutationFn: async (formData) => {
      const { SecondaryContact, fruitful_account_id } = viewAccount
      if (!SecondaryContact || !fruitful_account_id) {
        throw new Error('Missing account or contact information')
      }

      const existingContact = contactList.find((contact) => contact.id === SecondaryContact)
      if (!existingContact) {
        throw new Error('Contact not found')
      }

      const contactType = getContactType(existingContact)
      const [region, account_id] = fruitful_account_id.split('-')

      const init = getHttpInit('PUT')
      init.body = JSON.stringify({
        contact: {
          ContactType: !_.isEmpty(contactType) ? [contactType] : [],
          id: existingContact.id,
          firstname: formData.firstName?.trim(),
          lastname: formData.lastName?.trim(),
          title: formData.position?.trim(),
          phone: formData.officeNumber?.trim(),
          mobile: formData.mobileNumber?.trim(),
          email: formData.email?.trim(),
        },
      })

      const response = await fetch(
        `${env.API_URL}/api/accounts/${account_id}/contacts?region=${region}`,
        init,
      )

      if (!response.ok) {
        const error = await handleErrorResponse(response, queryClient, updateContactInfo).catch(
          (err) => err,
        )
        throw (
          error ||
          new Error(
            JSON.stringify({
              status: response.status,
              error_message: 'An error occurred while updating contact',
            }),
          )
        )
      }
      return response.json()
    },
    onSuccess: async (data) => {
      if (_.isEmpty(data)) return
      // Refetch contacts to update the UI
      await queryClient.invalidateQueries({
        queryKey: ['contacts', { accountId: viewAccount?.fruitful_account_id }],
      })
      setAlertComponent?.({
        open: true,
        message:
          data.Response === 'Success'
            ? 'Contact Updated Successfully'
            : 'An error occurred while updating contact',
        severity: data.Response === 'Success' ? 'success' : 'error',
      })
      onSuccess?.()
    },
    onError: DEFAULT_ERROR_HANDLER(setAlertComponent),
  })

  const handleSubmit = async (data) => {
    await updateContactInfo.mutateAsync(data)
  }

  return {
    defaultValues,
    handleSubmit,
    isLoading: updateContactInfo.isPending,
  }
}

export default useProfileSubmit
